// src/app/redux/sagas/skillsSaga.js

import { call, put } from "redux-saga/effects";
import Swal from "sweetalert2";
import {
    FETCH_SKILLS_SUCCESS, FETCH_SKILLS_FAIL,
    FETCH_SKILLS_BY_TYPE_SUCCESS, FETCH_SKILLS_BY_TYPE_FAIL,
    CREATE_SKILL_SUCCESS, CREATE_SKILL_FAIL,
    UPDATE_SKILL_SUCCESS, UPDATE_SKILL_FAIL,
    DELETE_SKILL_SUCCESS, DELETE_SKILL_FAIL,
    DOWNLOAD_SKILLS_TEMPLATE_SUCCESS, DOWNLOAD_SKILLS_TEMPLATE_FAIL,
    IMPORT_SKILLS_EXCEL_SUCCESS, IMPORT_SKILLS_EXCEL_FAIL,
    EXPORT_SKILLS_SUCCESS, EXPORT_SKILLS_FAIL,
    fetchSkillsRequest,
} from "../actions/skillsActions";

import { API_BASE_URL, getRequest, postRequest, putRequest, deleteRequest, getBlobRequest } from "../../../api";

/* util partagé */
function downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a   = document.createElement("a");
    a.href     = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}

/* ======================
   FETCH ALL
====================== */
export function* fetchSkillsSaga() {
    const token = sessionStorage.getItem("token");
    try {
        const res = yield call(getRequest, {
            url: `${API_BASE_URL}/api/skills`,
            token,
        });
        yield put({ type: FETCH_SKILLS_SUCCESS, payload: res.data });
    } catch (e) {
        yield put({ type: FETCH_SKILLS_FAIL, error: e.response?.data?.message || e.message });
    }
}

/* ======================
   FETCH BY TYPE
====================== */
export function* fetchSkillsByTypeSaga(action) {
    const token     = sessionStorage.getItem("token");
    const skillType = action?.payload?.skillType;
    try {
        const res = yield call(getRequest, {
            url: `${API_BASE_URL}/api/skills/type/${skillType}`,
            token,
        });
        yield put({ type: FETCH_SKILLS_BY_TYPE_SUCCESS, payload: res.data });
    } catch (e) {
        yield put({ type: FETCH_SKILLS_BY_TYPE_FAIL, payload: e.response?.data?.message || e.message });
    }
}

/* ======================
   CREATE (JSON simple)
====================== */
export function* createSkillSaga(action) {
    const token = sessionStorage.getItem("token");
    try {
        const res = yield call(postRequest, {
            url:   `${API_BASE_URL}/api/skills`,
            data:  action.payload,
            token,
        });
        yield put({ type: CREATE_SKILL_SUCCESS, payload: res.data });
        Swal.fire({ icon: "success", title: "Succès", text: "Skill ajouté" });
        yield put(fetchSkillsRequest());
    } catch (e) {
        const msg = e.response?.data?.message || e.message || "Erreur création skill";
        yield put({ type: CREATE_SKILL_FAIL, error: msg });
        Swal.fire({ icon: "error", title: "Erreur", text: msg });
    }
}

/* ======================
   UPDATE (JSON simple)
====================== */
export function* updateSkillSaga(action) {
    const token      = sessionStorage.getItem("token");
    const { id, payload } = action.payload;
    try {
        const res = yield call(putRequest, {
            url:   `${API_BASE_URL}/api/skills/${id}`,
            data:  payload,
            token,
        });
        yield put({ type: UPDATE_SKILL_SUCCESS, payload: res.data });
        Swal.fire({ icon: "success", title: "Succès", text: "Skill mis à jour" });
        yield put(fetchSkillsRequest());
    } catch (e) {
        const msg = e.response?.data?.message || e.message || "Erreur update skill";
        yield put({ type: UPDATE_SKILL_FAIL, error: msg });
        Swal.fire({ icon: "error", title: "Erreur", text: msg });
    }
}

/* ======================
   DELETE
====================== */
export function* deleteSkillSaga(action) {
    const token = sessionStorage.getItem("token");
    const { id } = action.payload;
    try {
        yield call(deleteRequest, {
            url:   `${API_BASE_URL}/api/skills/${id}`,
            token,
        });
        yield put({ type: DELETE_SKILL_SUCCESS, payload: { id } });
        Swal.fire({ icon: "success", title: "Supprimé", text: "Skill supprimé" });
        yield put(fetchSkillsRequest());
    } catch (e) {
        const msg = e.response?.data?.message || e.message || "Erreur suppression skill";
        yield put({ type: DELETE_SKILL_FAIL, error: msg });
        Swal.fire({ icon: "error", title: "Erreur", text: msg });
    }
}

/* ======================
   TEMPLATE
====================== */
export function* downloadSkillsTemplateSaga() {
    const token = sessionStorage.getItem("token");
    try {
        const res = yield call(getBlobRequest, {
            url:   `${API_BASE_URL}/api/skills/template`,
            token: `Bearer ${token}`,
        });
        downloadBlob(new Blob([res.data]), "template_skills.xlsx");
        yield put({ type: DOWNLOAD_SKILLS_TEMPLATE_SUCCESS });
    } catch (e) {
        yield put({ type: DOWNLOAD_SKILLS_TEMPLATE_FAIL, error: e.response?.data || e.message });
        Swal.fire({ icon: "error", title: "Erreur", text: "Téléchargement template échoué" });
    }
}

/* ======================
   IMPORT EXCEL
====================== */
export function* importSkillsExcelSaga(action) {
    const token = sessionStorage.getItem("token");
    const { file, dryRun, action: act } = action.payload;
    try {
        const fd = new FormData();
        fd.append("file", file);

        const url = `${API_BASE_URL}/api/skills/import?dryRun=${dryRun ? "true" : "false"}&action=${act}`;

        const res = yield call(postRequest, { url, data: fd, token });

        yield put({ type: IMPORT_SKILLS_EXCEL_SUCCESS, payload: res.data });

        if (!dryRun) {
            Swal.fire({
                icon:  "success",
                title: "Import terminé",
                text:  `${res.data.createdCount} lignes traitées, ${res.data.skippedCount} ignorées`,
            });
            yield put(fetchSkillsRequest());
        } else {
            Swal.fire({
                icon:  "info",
                title: "Prévisualisation",
                text:  `${res.data.createdCount} lignes valides, ${res.data.skippedCount} erreurs`,
            });
        }
    } catch (e) {
        yield put({ type: IMPORT_SKILLS_EXCEL_FAIL, error: e.response?.data || e.message });
        Swal.fire({ icon: "error", title: "Erreur import", text: "Import Excel échoué" });
    }
}

/* ======================
   EXPORT
====================== */
export function* exportSkillsSaga() {
    const token = sessionStorage.getItem("token");
    const uid   = sessionStorage.getItem("uid");
    try {
        const res = yield call(getBlobRequest, {
            url:          `${API_BASE_URL}/api/skills/export`,
            token:        `Bearer ${token}`,
            extraHeaders: { uid },
        });
        downloadBlob(new Blob([res.data]), "export_skills.xlsx");
        yield put({ type: EXPORT_SKILLS_SUCCESS });
    } catch (e) {
        yield put({ type: EXPORT_SKILLS_FAIL });
        Swal.fire({ icon: "error", title: "Erreur", text: "Export échoué" });
    }
}